import { useCallback, useMemo, useState } from "react";

import { directUploadFile } from "./directUploadFile";

export const UPLOAD_STATUS_IN_PROGRESS = "IN_PROGRESS";
export const UPLOAD_STATUS_ERROR = "ERROR";
export const UPLOAD_STATUS_COMPLETED = "COMPLETED";

// This hook is used to manage the process of uploading a file directly to the storage
// service, handling the creation of the blob on the backend server, fetching the direct
// upload URL and headers, and uploading the file to the storage service through
// Rails' ActiveStorage direct upload mechanisms.
//
export const useDirectFileUpload = props => {
  const { file, attachmentToken, onUploadStart, onUploadComplete, onUploadError } = props;
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS_IN_PROGRESS);
  const [uploadInitialized, setUploadInitialized] = useState(false);

  const isNotUploaded = useMemo(
    () => !(file.key || uploadInitialized),
    [file.key, uploadInitialized]
  );

  const handleTransferProgress = useCallback(progress => {
    setUploadProgress(progress);
  }, []);

  // When the upload is complete, we send a PATCH request to the server to notify that the
  // upload is done, and we call the onUploadComplete callback with the data returned by the
  // server, which will contain the needed data to attach the blob to the record.
  const handleUploadCompleted = useCallback(
    async data => {
      setUploadProgress(100);
      onUploadComplete(data);
    },
    [onUploadComplete]
  );

  // Prepare the upload by creating the blob in the backend, and fetching the direct
  // upload URL and headers from the server. When the upload is ready, we start the
  // upload process.
  //
  // This function is called when the component is mounted, and only once.
  const startUpload = useCallback(async () => {
    setUploadInitialized(true);

    try {
      await directUploadFile({
        file,
        attachmentToken,
        onProgress: handleTransferProgress,
        onUploadStart,
        onComplete: handleUploadCompleted
      });

      setUploadStatus(UPLOAD_STATUS_COMPLETED);
    } catch (error) {
      setUploadStatus(UPLOAD_STATUS_ERROR);

      onUploadError(error);
    }
  }, [
    file,
    attachmentToken,
    handleTransferProgress,
    handleUploadCompleted,
    onUploadError,
    onUploadStart
  ]);

  // Send the file if it's not uploaded, as soon as the component is mounted
  const upload = useCallback(() => {
    isNotUploaded && startUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotUploaded, startUpload]);

  return { upload, uploadStatus, uploadProgress };
};
